import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Language } from '@/types';
import { Card, Button, Input, Textarea, Alert } from '@/components/common';
import { getTranslation } from '@/constants';
import { useAuth } from '@/context/AuthContext';
import { useProfile } from '@/hooks';
import { getStatusDisplay, getStatusProgress } from '@/utils';

interface ProfilePageProps {
  language: Language;
}

export const ProfilePage: React.FC<ProfilePageProps> = ({ language }) => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const { updateProfile, uploadPhoto, loading } = useProfile();
  const t = getTranslation(language);

  const [editing, setEditing] = useState(false);
  const [displayName, setDisplayName] = useState(userProfile?.displayName || '');
  const [bio, setBio] = useState(userProfile?.bio || '');
  const [phoneNumber, setPhoneNumber] = useState(userProfile?.phoneNumber || '');
  const [postalCode, setPostalCode] = useState(userProfile?.postalCode || '');
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!user || !userProfile) {
    return null;
  }

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB');
        return;
      }
      setPhotoFile(file);
    }
  };

  const handleSave = async () => {
    try {
      setError(null);

      // Upload photo if selected
      if (photoFile) {
        await uploadPhoto(user.uid, photoFile);
      }

      // Update profile
      await updateProfile(user.uid, {
        displayName,
        bio,
        phoneNumber,
        postalCode: postalCode.toUpperCase().slice(0, 3), // Store only first 3 chars (FSA)
      });

      setSuccess(true);
      setEditing(false);
      setPhotoFile(null);

      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError('Failed to update profile. Please try again.');
    }
  };

  const statusDisplay = getStatusDisplay(userProfile.communityStatus, language);
  const statusProgress = getStatusProgress(userProfile.totalExchanges);

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      {/* Back Button */}
      <button
        onClick={() => navigate('/')}
        className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors py-2"
      >
        <span className="text-lg">←</span>
        {language === 'en' ? 'Back to Home' : 'Retour à l\'accueil'}
      </button>

      {success && <Alert variant="success">{t.success}</Alert>}
      {error && (
        <Alert variant="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">{t.profile}</h2>
          {!editing && (
            <Button size="sm" onClick={() => setEditing(true)}>{t.editProfile}</Button>
          )}
        </div>

        <div className="space-y-6">
          {/* Photo */}
          <div className="flex items-center space-x-4">
            {userProfile.photoURL || photoFile ? (
              <img
                src={photoFile ? URL.createObjectURL(photoFile) : userProfile.photoURL}
                alt={userProfile.displayName || 'Profile'}
                className="w-24 h-24 rounded-full object-cover"
              />
            ) : (
              <div className="w-24 h-24 rounded-full bg-blue-100 flex items-center justify-center">
                <span className="text-4xl text-blue-600 font-semibold">
                  {(userProfile.displayName || userProfile.email).charAt(0).toUpperCase()}
                </span>
              </div>
            )}

            {editing && (
              <div>
                <label
                  htmlFor="photo-upload"
                  className="cursor-pointer inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  {t.uploadPhoto}
                </label>
                <input
                  id="photo-upload"
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  className="hidden"
                />
                {photoFile && (
                  <p className="mt-2 text-sm text-green-600">
                    {language === 'en' ? 'Photo selected' : 'Photo sélectionnée'}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Community Status */}
          <div className="bg-gradient-to-r from-blue-50 to-green-50 rounded-xl p-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {language === 'en' ? 'Community Status' : 'Statut communautaire'}
            </label>
            <div className="flex items-center space-x-3 mb-3">
              <span className="text-3xl">{statusDisplay}</span>
            </div>

            {/* Exchange count */}
            <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
              <span className="font-semibold text-blue-600">{userProfile.totalExchanges}</span>
              <span>{language === 'en' ? 'neighbors helped' : 'voisins aidés'}</span>
            </div>

            {/* Progress to next level */}
            {statusProgress.next && (
              <div className="mt-3">
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>
                    {language === 'en' ? 'Progress to next level' : 'Progression vers le niveau suivant'}
                  </span>
                  <span>
                    {statusProgress.remaining} {language === 'en' ? 'more to go' : 'restants'}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-blue-500 to-green-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${statusProgress.progress}%` }}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {language === 'en' ? 'Next: ' : 'Prochain: '}
                  {getStatusDisplay(statusProgress.next, language)}
                </p>
              </div>
            )}

            {!statusProgress.next && (
              <p className="text-sm text-green-600 font-medium">
                {language === 'en'
                  ? '🎉 You\'ve reached the highest level!'
                  : '🎉 Vous avez atteint le niveau le plus élevé!'}
              </p>
            )}
          </div>

          {/* Email */}
          <Input
            label={t.email}
            type="email"
            value={userProfile.email}
            disabled
          />

          {/* Display Name */}
          <Input
            label={t.displayName}
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            disabled={!editing}
          />

          {/* Bio */}
          <Textarea
            label={t.bio}
            value={bio}
            onChange={(e) => setBio(e.target.value)}
            disabled={!editing}
            rows={4}
            maxLength={300}
            helperText={editing ? `${bio.length}/300` : undefined}
          />

          {/* Phone Number */}
          <Input
            label={t.phoneNumber}
            type="tel"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            disabled={!editing}
          />

          {/* Postal Code (FSA) */}
          <div>
            <Input
              label={language === 'en' ? 'Postal Code (First 3 digits)' : 'Code postal (3 premiers caractères)'}
              type="text"
              value={postalCode}
              onChange={(e) => setPostalCode(e.target.value.toUpperCase().slice(0, 3))}
              disabled={!editing}
              placeholder={language === 'en' ? 'e.g. H2X' : 'ex. H2X'}
              maxLength={3}
            />
            <p className="mt-1 text-xs text-gray-500">
              {language === 'en'
                ? 'Used to show your approximate location to others'
                : 'Utilisé pour afficher votre emplacement approximatif'}
            </p>
          </div>

          {/* Actions */}
          {editing && (
            <div className="flex space-x-3 pt-4 border-t">
              <Button
                variant="secondary"
                fullWidth
                onClick={() => {
                  setEditing(false);
                  setDisplayName(userProfile.displayName || '');
                  setBio(userProfile.bio || '');
                  setPhoneNumber(userProfile.phoneNumber || '');
                  setPostalCode(userProfile.postalCode || '');
                  setPhotoFile(null);
                }}
              >
                {t.cancel}
              </Button>
              <Button fullWidth onClick={handleSave} loading={loading}>
                {t.saveChanges}
              </Button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};
